import { get, post, put, patch, del } from './api';
import type {
  LineupSlot,
  LineupVersus,
  RosterSettingsRequest,
  Scholarship,
  ScholarshipLineupRequest,
  ScholarshipLineups,
  ScholarshipConfigurationRequest,
  ScholarshipDuplicateRequest,
  ScholarshipListItem,
  ScholarshipNameRequest,
  ScholarshipPlayer,
  ScholarshipPlayerPosition,
  ScholarshipPlayerWriteRequest,
  ScholarshipReorderRequest,
  ScholarshipTransferPlayerRequest,
  RecruitingStatus,
  ScholarshipAttachProspectsRequest,
  ScholarshipSetRecruitingStatusRequest,
  ScholarshipVisibleCardsRequest,
} from '../types/scholarships';

export const listScholarships = (includeArchived = false) =>
  get<ScholarshipListItem[]>(`/api/scholarships?includeArchived=${includeArchived}`);

export const getScholarship = (id: string) =>
  get<Scholarship>(`/api/scholarships/${id}`);

export const createScholarship = (body: ScholarshipNameRequest) =>
  post<Scholarship>('/api/scholarships', body);

export const renameScholarship = (id: string, body: ScholarshipNameRequest) =>
  put<Scholarship>(`/api/scholarships/${id}`, body);

export const updateScholarshipConfiguration = (
  id: string,
  body: ScholarshipConfigurationRequest,
) => put<Scholarship>(`/api/scholarships/${id}/configuration`, body);

// Which summary cards show above the board. Separate from updateScholarshipConfiguration
// on purpose: card choice is a view preference, and folding it into the config payload
// would mean every save from Configure roster silently overwrote it.
export const updateScholarshipCards = (id: string, visibleCards: string[]) =>
  put<Scholarship>(
    `/api/scholarships/${id}/cards`,
    { visibleCards } satisfies ScholarshipVisibleCardsRequest,
  );

// Attaches players from the prospect pool. All-or-nothing server-side: if any id is not
// visible to the org nothing is written, so a rejection means the board is unchanged.
// Returns the WHOLE roster, because sort order only means anything within a full position pool.
export const attachProspectsToScholarship = (id: string, prospectIds: string[]) =>
  post<ScholarshipPlayer[]>(
    `/api/scholarships/${id}/players/attach`,
    { prospectIds } satisfies ScholarshipAttachProspectsRequest,
  );

// Writes the PLAYER RECORD's recruiting status, reached through the board because that is
// the only write path for it. A manual row has no player record and gets a 400.
export const setScholarshipPlayerRecruitingStatus = (
  scholarshipId: string,
  playerId: string,
  recruitingStatus: RecruitingStatus,
) =>
  put<ScholarshipPlayer>(
    `/api/scholarships/${scholarshipId}/players/${playerId}/status`,
    { recruitingStatus } satisfies ScholarshipSetRecruitingStatusRequest,
  );

/*
  Sets the order the boards list renders in. Global: one sequence across both purposes, so the
  body carries EVERY non-archived board even when the coach only moved one inside a section.

  A 409 (`BoardSetChanged`) means the caller's set no longer matches the server's -- another
  coach created, archived or deleted a board. Refetch and let them try again rather than retrying
  this order, which no longer describes the list. Returns the boards in the new order.
*/
export const reorderScholarships = (orderedIds: string[]) =>
  patch<ScholarshipListItem[]>(
    '/api/scholarships/reorder',
    { orderedIds } satisfies ScholarshipReorderRequest,
  );

export const duplicateScholarship = (id: string, advanceClass = false) =>
  post<Scholarship>(`/api/scholarships/${id}/duplicate`, { advanceClass } satisfies ScholarshipDuplicateRequest);

export const archiveScholarship = (id: string) =>
  post<void>(`/api/scholarships/${id}/archive`, {});

export const restoreScholarship = (id: string) =>
  post<void>(`/api/scholarships/${id}/restore`, {});

export const deleteScholarship = (id: string) =>
  del<void>(`/api/scholarships/${id}`);

export const listScholarshipPlayers = (scholarshipId: string) =>
  get<ScholarshipPlayer[]>(`/api/scholarships/${scholarshipId}/players`);

export const createScholarshipPlayer = (
  scholarshipId: string,
  body: ScholarshipPlayerWriteRequest,
) => post<ScholarshipPlayer>(`/api/scholarships/${scholarshipId}/players`, body);

export const updateScholarshipPlayer = (
  scholarshipId: string,
  playerId: string,
  body: ScholarshipPlayerWriteRequest,
) => put<ScholarshipPlayer>(`/api/scholarships/${scholarshipId}/players/${playerId}`, body);

/*
  Moves a row to another board: the target gains it (carrying the link to the player record), and
  the source loses it. Not a copy -- a committed recruit belongs on the roster now, not on both.

  Returns the SOURCE board's remaining players, because that is the screen the coach is looking
  at. A 409 (`AlreadyOnTargetBoard`) means the target already holds that player; there is nothing
  to retry, so say so rather than failing silently.
*/
export const transferScholarshipPlayer = (
  scholarshipId: string,
  playerId: string,
  targetScholarshipId: string,
) =>
  post<ScholarshipPlayer[]>(
    `/api/scholarships/${scholarshipId}/players/${playerId}/transfer`,
    { targetScholarshipId } satisfies ScholarshipTransferPlayerRequest,
  );

export const removeScholarshipPlayer = (scholarshipId: string, playerId: string) =>
  del<void>(`/api/scholarships/${scholarshipId}/players/${playerId}`);

export const reorderScholarshipPlayers = (
  scholarshipId: string,
  position: ScholarshipPlayerPosition,
  orderedIds: string[],
) =>
  patch<void>(`/api/scholarships/${scholarshipId}/players/reorder`, {
    position,
    orderedIds,
  });


// The board's option lists (traits, indicators, handedness colours, pitching lists, player types
// and statuses). A full replace; returns the whole board so the caller re-renders from one source.
export const updateRosterSettings = (id: string, body: RosterSettingsRequest) =>
  put<Scholarship>(`/api/scholarships/${id}/settings`, body);

// Back to the API's defaults. The board then reports rosterSettingsCustomized: false.
export const resetRosterSettings = (id: string) =>
  del<Scholarship>(`/api/scholarships/${id}/settings`);

export const getScholarshipLineups = (scholarshipId: string) =>
  get<ScholarshipLineups>(`/api/scholarships/${scholarshipId}/lineups`);

// Replaces ONE side's lineup and returns both. Send one at a time per side (useLineups queues):
// a 409 means another replace of the same side landed first, so refetch that side rather than
// retry blind; a 404 (`unknownPlayerIds`) means a slot names a player no longer on the board, so
// refetch the players as well, or the stale row is sent again on every save.
export const updateScholarshipLineup = (
  scholarshipId: string,
  versus: LineupVersus,
  slots: LineupSlot[],
) =>
  put<ScholarshipLineups>(
    `/api/scholarships/${scholarshipId}/lineups/${versus}`,
    { slots } satisfies ScholarshipLineupRequest,
  );
